import { resolveMediaUrl } from "@utils/media/mediaUrl";

function toFiniteNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function readCoordinate(entity, keys) {
  if (!entity) return null;
  for (const key of keys) {
    const value = toFiniteNumber(entity[key]);
    if (value !== null) return value;
  }
  return null;
}

function guessMimeTypeFromUrl(url = "") {
  const value = String(url || "").toLowerCase();
  if (value.endsWith(".mp4")) return "video/mp4";
  if (value.endsWith(".webm")) return "video/webm";
  if (value.endsWith(".ogg")) return "video/ogg";
  if (value.endsWith(".mp3")) return "audio/mpeg";
  if (value.endsWith(".wav")) return "audio/wav";
  if (value.endsWith(".m4a")) return "audio/mp4";
  if (value.endsWith(".aac")) return "audio/aac";
  return "";
}

export function normalizePromoVideoMultimedia(tourPromoVideoRaw = {}) {
  if (!tourPromoVideoRaw || typeof tourPromoVideoRaw !== "object") return [];
  const directId = tourPromoVideoRaw?.multimediaId || tourPromoVideoRaw?.id || "";
  if (directId) {
    const directUrl = resolveMediaUrl(tourPromoVideoRaw?.url || "");
    return [{
      id: directId,
      sourceId: directId,
      url: directUrl,
      mediaType: "video",
      mediaRole: "promoVideo",
      languageIso: "en",
      mimeType: guessMimeTypeFromUrl(directUrl),
    }];
  }
  return Object.entries(tourPromoVideoRaw).reduce((acc, [iso, entry]) => {
    const resolvedIso = String(iso || "").toLowerCase();
    const itemId = entry?.multimediaId || entry?.id || "";
    const itemUrl = resolveMediaUrl(entry?.url || "");
    if (!resolvedIso || !itemId) return acc;
    acc.push({
      id: `${itemId}-${resolvedIso}`,
      sourceId: itemId,
      url: itemUrl,
      mediaType: "video",
      mediaRole: "promoVideo",
      languageIso: resolvedIso,
      mimeType: guessMimeTypeFromUrl(itemUrl),
    });
    return acc;
  }, []);
}

export function normalizePromoAudioMultimedia(tourAudioRaw = []) {
  return (tourAudioRaw || []).reduce((acc, item, index) => {
    const audioUrls = item?.audioUrls || {};
    const title = item?.title || {};
    const groupId = item?.id || item?.audioGroupId || item?.multimediaId || `audio-${index + 1}`;
    const baseId = item?.multimediaId || item?.id || groupId;
    const entries = Object.entries(audioUrls || {});
    if (!entries.length && (item?.url || "")) {
      acc.push({
        id: `${baseId}-en`,
        sourceId: baseId,
        audioGroupId: groupId,
        url: resolveMediaUrl(item?.url || ""),
        mediaType: "audio",
        mediaRole: "promoAudio",
        languageIso: "en",
        mimeType: guessMimeTypeFromUrl(item?.url || ""),
        audioTitles: title,
      });
      return acc;
    }
    entries.forEach(([iso, url]) => {
      const resolvedIso = String(iso || "").toLowerCase();
      if (!resolvedIso || !url) return;
      const resolvedUrl = resolveMediaUrl(url);
      acc.push({
        id: `${baseId}-${resolvedIso}`,
        sourceId: baseId,
        audioGroupId: groupId,
        url: resolvedUrl,
        mediaType: "audio",
        mediaRole: "promoAudio",
        languageIso: resolvedIso,
        mimeType: guessMimeTypeFromUrl(resolvedUrl),
        audioTitles: { [resolvedIso]: title?.[resolvedIso] || "" },
      });
    });
    return acc;
  }, []);
}

export function normalizeStartPoints(tour) {
  const candidates = [
    tour?.startPoints,
    tour?.description?.startPoints,
    tour?.description?.start_points,
    tour?.description?.tourStartPoints,
  ];
  const source = candidates.find((item) => Array.isArray(item)) || [];
  return source.map((point, index) => {
    const lng = readCoordinate(point, ["lng", "longitude", "lon"]);
    const lat = readCoordinate(point, ["lat", "latitude"]);
    if (lng === null || lat === null) return null;
    return {
      id: point?.id || `start-${index + 1}`,
      internalId: point?.internalId || point?.stp_id || point?.stpId || point?.uuid || "",
      isPersisted: true,
      order: Number(point?.order) || index + 1,
      longitude: lng,
      latitude: lat,
      name: point?.name || point?.zoneName || point?.placeName || point?.locationName || "",
      status: Number(point?.status) || 1,
      isActive: typeof point?.isActive === "boolean" ? point.isActive : true,
      description: point?.description || "",
      modes: Array.isArray(point?.modes) ? point.modes : [],
      price: {
        currencyIso: point?.price?.currencyIso || point?.currencyIso || "USD",
        amount: Number(point?.price?.amount ?? point?.amount) || 0,
      },
    };
  }).filter(Boolean);
}
